import React, { useState, useEffect } from 'react';
import { useNavigation } from '../../context/NavigationContext';
import { BringzzPageContainer, BringzzIcon, BringzzText } from "@bringzz/components";
import { toast } from 'react-toastify';
import useRequest from '../../hooks/useRequest';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import NotificationItem from './components/NotificationItem';
import EmptyState from './components/EmptyState';
import FilterButtons from './components/FilterButtons';
import { parseNotificationContent, parseNotificationDateForSort } from './utils';

const NotificationsPage = () => {
    const { backPress, navigate } = useNavigation();
    const { sendRequest, loading } = useRequest();
    const { user } = useAuth();
    const [fetchedNotifications, setFetchedNotifications] = useState([]);
    const [deletingNotifications, setDeletingNotifications] = useState({});
    const [markingAsRead, setMarkingAsRead] = useState({});
    const [activeFilter, setActiveFilter] = useState('all');

    useEffect(() => {
        const fetchNotifications = async () => {
            if (user && user.id) {
                try {
                    const response = await sendRequest({
                        method: 'GET',
                        url: '/module/notification/fetchNotificationsByUser',
                    });

                    if (response?.status === 'success') {
                        // Sort notifications by date (newest first)
                        const sortedNotifications = (response.data || []).sort((a, b) => {
                            const dateA = parseNotificationDateForSort(a.created);
                            const dateB = parseNotificationDateForSort(b.created);
                            return dateB - dateA; // Newest first
                        });
                        setFetchedNotifications(sortedNotifications);
                    } else if (response?.status === 'error' && response?.message === 'No notifications found') {
                        setFetchedNotifications([]);
                    } else {
                        toast.error('Failed to load notifications');
                    }
                } catch (error) {
                    console.error('Error fetching notifications:', error);
                    toast.error('Error loading notifications');
                }
            }
        };

        fetchNotifications();
    }, [user?.id]);

    const handleDeleteNotification = async (notificationId) => {
        try {
            setDeletingNotifications(prev => ({ ...prev, [notificationId]: true }));

            const response = await sendRequest({
                method: 'POST',
                url: '/module/notification/deleteNotification',
                data: {
                    notification_id: notificationId
                }
            });

            if (response?.status === 'success') {
                setFetchedNotifications(prev =>
                    prev.filter(notification => notification.id !== notificationId)
                );
                // toast.success(response.message || 'Notification deleted successfully');
            } else {
                toast.error('Failed to delete notification');
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
            toast.error('Error deleting notification');
        } finally {
            setDeletingNotifications(prev => ({ ...prev, [notificationId]: false }));
        }
    };

    const handleMarkAsRead = async (notificationIds) => {
        // Declare validIds in function scope so it's accessible in finally block
        let validIds = [];

        try {
            if (!Array.isArray(notificationIds)) {
                notificationIds = [notificationIds];
            }

            // Filter out any invalid IDs
            validIds = notificationIds.filter(id => id && typeof id === 'number');
            if (validIds.length === 0) {
                toast.error('No valid notification IDs provided');
                return false;
            }

            console.log('Marking notifications as read:', validIds);

            // Mark notifications as loading
            const loadingState = {};
            validIds.forEach(id => {
                loadingState[id] = true;
            });
            setMarkingAsRead(prev => ({ ...prev, ...loadingState }));

            const response = await sendRequest({
                method: 'POST',
                url: '/module/notification/markAsRead',
                data: {
                    notification_ids: validIds
                }
            });

            if (response?.status === 'success') {
                // Update local state to mark notifications as read
                setFetchedNotifications(prev =>
                    prev.map(notification =>
                        validIds.includes(notification.id)
                            ? { ...notification, read: 1 }
                            : notification
                    )
                );
                // toast.success(response.message || 'Notifications marked as read');
                return true;
            } else {
                toast.error('Failed to mark notifications as read');
                return false;
            }
        } catch (error) {
            console.error('Error marking notifications as read:', error);
            toast.error('Error marking notifications as read');
            return false;
        } finally {
            // Clear loading state
            const clearLoadingState = {};
            validIds.forEach(id => {
                clearLoadingState[id] = false;
            });
            setMarkingAsRead(prev => ({ ...prev, ...clearLoadingState }));
        }
    };

    const handleMarkAllAsRead = async () => {
        const unreadNotifications = fetchedNotifications.filter(n => n.read === 0 && n.id);
        if (unreadNotifications.length > 0) {
            const unreadIds = unreadNotifications.map(n => n.id);
            await handleMarkAsRead(unreadIds);
        }
    };

    const handleNotificationClick = async (notification) => {
        // Mark as read if unread and has valid ID
        if (notification.read === 0 && notification.id) {
            const success = await handleMarkAsRead(notification.id);
            if (!success) return; // Don't proceed if marking as read failed
        }

        // Execute specific function based on notification type
        if (notification.notification_type === 'ORDER_REQUEST') {
            handleOrderRequestNotification(notification);
        } else if (notification.notification_type === 'CHAT_MESSAGE') {
            navigate('/settings/chats');
        } else if (notification.notification_type === 'ORDER_PAID' || notification.notification_type?.includes('ORDER_PROGRESS')) {
            navigate('/home');
        }
    };

    const getOrderDetailsUrl = (uuid) => {
        if (user?.userRole === USER_ROLES.PHARMACY_ROLE) {
            return `/settings/pharmacy/orders/view/${uuid}`;
        } else if (user?.userRole === USER_ROLES.SHOPPER_ROLE) {
            return `/settings/shopper/orders/view/${uuid}`;
        } else if (user?.userRole === USER_ROLES.CONSUMER_ROLE) {
            return `/settings/consumer/orders/view/${uuid}`;
        }
        // Fallback to generic settings URL
        return `/settings/orders/view/${uuid}`;
    };

    const handleOrderRequestNotification = (notification) => {
        try {
            const parsedContent = parseNotificationContent(notification.content);
            if (parsedContent.uuid) {
                // Navigate to order details page based on user role
                const orderUrl = getOrderDetailsUrl(parsedContent.uuid);
                navigate(orderUrl);
            } else {
                toast.error('Order details not available');
            }
        } catch (error) {
            console.error('Error handling order request notification:', error);
            toast.error('Unable to open order details');
        }
    };

    const filterNotifications = (notificationsList) => {
        if (activeFilter === 'all') {
            return notificationsList;
        } else if (activeFilter === 'orders') {
            return notificationsList.filter(n => n.notification_type?.startsWith('ORDER_'));
        } else if (activeFilter === 'chats') {
            return notificationsList.filter(n => n.notification_type === 'CHAT_MESSAGE');
        } else if (activeFilter === 'others') {
            return notificationsList.filter(n =>
                !n.notification_type?.startsWith('ORDER_') &&
                n.notification_type !== 'CHAT_MESSAGE'
            );
        }
        return notificationsList;
    };

    // Only use fetched notifications from API - context notifications are for real-time alerts only
    const allNotifications = fetchedNotifications;
    const filteredNotifications = filterNotifications(allNotifications);
    const unreadCount = fetchedNotifications.filter(n => n.read === 0).length;

    return (
        <BringzzPageContainer
            header={{
                left: (
                    <BringzzIcon
                        size='22'
                        icon='IconChevronLeft'
                        folder='LineIcons'
                        className='cursor-pointer'
                        onClick={backPress}
                    />
                ),
                title: <BringzzText tag='h2'>Notifications</BringzzText>,
                right: unreadCount > 0 ? (
                    <BringzzIcon
                        size='20'
                        icon='IconCheckmark'
                        folder='LineIcons'
                        className='cursor-pointer text-magic-lilac hover:text-magic-lilac/80'
                        onClick={handleMarkAllAsRead}
                        title='Mark all as read'
                    />
                ) : null
            }}
        >
            {loading ? (
                <div className="flex-grow flex items-center justify-center p-8">
                    <BringzzIcon
                        size="32"
                        icon="IconSpinner"
                        folder="LineIcons"
                        className="animate-spin text-magic-lilac"
                    />
                </div>
            ) : (
                <div className="p-6 min-h-screen">
                    {allNotifications.length > 0 && (
                        <FilterButtons
                            activeFilter={activeFilter}
                            onFilterChange={setActiveFilter}
                        />
                    )}
                    {filteredNotifications.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div className="space-y-4 pb-20">
                            {filteredNotifications.map((notification, index) => {
                                const isUnread = notification.read === 0;
                                const uniqueKey = notification.id ? `db-${notification.id}` : `ctx-${index}`;

                                return (
                                    <NotificationItem
                                        key={uniqueKey}
                                        notification={notification}
                                        uniqueKey={uniqueKey}
                                        isUnread={isUnread}
                                        markingAsRead={markingAsRead}
                                        deletingNotifications={deletingNotifications}
                                        onNotificationClick={handleNotificationClick}
                                        onDeleteNotification={handleDeleteNotification}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </BringzzPageContainer>
    );
};

export default NotificationsPage;